/**
 * Submit forms when Enter is pressed in single-line fields.
 *
 * Replaces implicit submission with an explicit requestSubmit() on the
 * primary submit control so behaviour stays correct when multiple submit
 * buttons exist (Drupal: secondary first, primary last) or when native
 * implicit submission is unreliable.
 */

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLButtonElement|HTMLInputElement|null}
 */
function findPrimarySubmitter(form) {
  /** @type {(HTMLButtonElement|HTMLInputElement)[]} */
  const controls = [];

  form.querySelectorAll('input[type="submit"]:not(:disabled)').forEach((el) => {
    controls.push(el);
  });
  form.querySelectorAll('input[type="image"]:not(:disabled)').forEach((el) => {
    controls.push(el);
  });
  form.querySelectorAll('button:not([disabled])').forEach((el) => {
    const t = el.getAttribute('type');
    if (t === 'button' || t === 'reset') {
      return;
    }
    controls.push(el);
  });

  if (controls.length === 0) {
    return null;
  }
  if (controls.length === 1) {
    return controls[0];
  }

  const primary = controls.find((c) =>
    c.matches('.button--primary, .button-primary, .btn-primary, .webform-button--submit'),
  );
  if (primary) {
    return primary;
  }

  const actions = form.querySelector('.form-actions');
  if (actions) {
    const inActions = controls.filter((c) => actions.contains(c));
    if (inActions.length > 0) {
      return inActions[inActions.length - 1];
    }
  }

  return controls[controls.length - 1];
}

/**
 * @param {EventTarget|null} target
 * @returns {boolean}
 */
function shouldHandleEnterOnInput(target) {
  if (!(target instanceof HTMLInputElement)) {
    return false;
  }
  const blocked = new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'reset',
    'submit',
    'range',
  ]);
  const type = (target.type || 'text').toLowerCase();
  return !blocked.has(type);
}

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isInsideExcludedWidget(el) {
  return Boolean(
    el.closest(
      '.select2-container, .chosen-container, .ui-autocomplete, .ck-editor__editable, .ck.ck-editor__editable',
    ),
  );
}

export function initZkFormEnterSubmit() {
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) {
        return;
      }
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      if (target.matches('textarea, select, [contenteditable="true"]')) {
        return;
      }
      if (!shouldHandleEnterOnInput(target)) {
        return;
      }
      if (isInsideExcludedWidget(target)) {
        return;
      }

      const form = target.form;
      if (!form || !(form instanceof HTMLFormElement)) {
        return;
      }

      const submitter = findPrimarySubmitter(form);
      if (!submitter) {
        return;
      }

      event.preventDefault();
      // Drupal #ajax submit buttons listen for `click`, not the form `submit` event.
      if (submitter.matches('.use-ajax-submit')) {
        submitter.click();
        return;
      }
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
      }
      else {
        submitter.click();
      }
    },
    false,
  );
}
